import React, { useState, useRef, useEffect, useCallback } from 'react';
import { DateRange, PRESET_DATE_RANGES } from '../../../constants/ui';
import { getDateRangeFilter, parseCustomUtcRange } from '../../../utils/dateUtils';
import { getHolidayName, isWeekend } from '../../../utils/holidays';
import { CalendarIcon } from '../../ui/Icons';

interface DateRangeSelectorProps {
  dateRange: DateRange;
  customDateRange: { startDate: Date; endDate: Date } | null;
  onDateRangeChange: (range: DateRange) => void;
  onCustomRangeApply: (startDate: string, endDate: string) => void;
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = React.memo(({
  dateRange,
  customDateRange,
  onDateRangeChange,
  onCustomRangeApply,
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(() =>
    customDateRange ? customDateRange.startDate.toISOString().split('T')[0] : ''
  );
  const [customEndDate, setCustomEndDate] = useState(() =>
    customDateRange ? customDateRange.endDate.toISOString().split('T')[0] : ''
  );
  const [calendarButtonPosition, setCalendarButtonPosition] = useState<{ top: number; left: number } | null>(null);
  const calendarButtonRef = useRef<HTMLButtonElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Click-outside handler
  useEffect(() => {
    if (!showDatePicker) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        datePickerRef.current && calendarButtonRef.current &&
        !datePickerRef.current.contains(target) &&
        !calendarButtonRef.current.contains(target)
      ) {
        setShowDatePicker(false);
        setCalendarButtonPosition(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDatePicker]);

  const handleToggleDatePicker = useCallback(() => {
    if (!showDatePicker && calendarButtonRef.current) {
      const rect = calendarButtonRef.current.getBoundingClientRect();
      setCalendarButtonPosition({ top: rect.bottom + 8, left: rect.left });
    }
    setShowDatePicker(prev => !prev);
  }, [showDatePicker]);

  const handleApplyCustomRange = useCallback(() => {
    if (customStartDate && customEndDate) {
      onCustomRangeApply(customStartDate, customEndDate);
      setShowDatePicker(false);
      setCalendarButtonPosition(null);
    }
  }, [customStartDate, customEndDate, onCustomRangeApply]);

  const handleCancel = useCallback(() => {
    setShowDatePicker(false);
    setCalendarButtonPosition(null);
  }, []);

  return (
    <>
      {/* Compact Period Buttons */}
      <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
        <div className="flex items-center gap-1 flex-shrink-0">
          {PRESET_DATE_RANGES.map((range) => {
            const { label } = getDateRangeFilter(range);
            const shortLabel = label.replace('Last ', '').replace(' Days', 'd').replace('This Month', 'Month').replace('Last Hour', '1h').replace(' Hours', 'h');
            return (
              <button
                key={range}
                onClick={() => onDateRangeChange(range)}
                className={`px-1.5 sm:px-2 py-1 text-[10px] sm:text-xs font-medium rounded transition-all whitespace-nowrap ${
                  dateRange === range && !customDateRange
                    ? 'bg-slate-700 dark:bg-slate-600 text-white shadow-sm'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                {shortLabel}
              </button>
            );
          })}
          <button
            ref={calendarButtonRef}
            onClick={handleToggleDatePicker}
            className={`p-1 rounded transition-all ${
              showDatePicker
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
            }`}
            title="Custom date range"
          >
            <CalendarIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Date Picker Modal */}
      {showDatePicker && calendarButtonPosition && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" />

          {/* Modal */}
          <div
            ref={datePickerRef}
            className="fixed z-50 date-picker"
            style={{
              top: window.innerWidth < 640 ? '50%' : `${calendarButtonPosition.top}px`,
              left: window.innerWidth < 640 ? '50%' : `${calendarButtonPosition.left}px`,
              transform: window.innerWidth < 640
                ? 'translate(-50%, -50%)'
                : (() => {
                    if (typeof window !== 'undefined') {
                      const rightEdge = calendarButtonPosition.left + 320;
                      if (rightEdge > window.innerWidth) {
                        return `translateX(-${rightEdge - window.innerWidth + 16}px)`;
                      }
                    }
                    return 'none';
                  })()
            }}
          >
            <div className="bg-white dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-600 shadow-xl w-[calc(100vw-2rem)] sm:w-auto max-w-sm">
              <div className="space-y-4">
                {/* Holiday Legend */}
                <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-amber-500 rounded"></div>
                    <span className="text-slate-600 dark:text-slate-400">Japanese Holidays</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-slate-400 rounded"></div>
                    <span className="text-slate-600 dark:text-slate-400">Weekends</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">From</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-full text-sm p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                    {customStartDate && (
                      <>
                        {getHolidayName(customStartDate) && (
                          <div className="absolute -top-8 left-0 px-2 py-1 bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 text-xs rounded shadow-md whitespace-nowrap">
                            {getHolidayName(customStartDate)}
                          </div>
                        )}
                        {isWeekend(customStartDate) && !getHolidayName(customStartDate) && (
                          <div className="absolute -top-8 left-0 px-2 py-1 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs rounded shadow-md">
                            Weekend
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">To</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-full text-sm p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                    {customEndDate && (
                      <>
                        {getHolidayName(customEndDate) && (
                          <div className="absolute -top-8 left-0 px-2 py-1 bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 text-xs rounded shadow-md whitespace-nowrap">
                            {getHolidayName(customEndDate)}
                          </div>
                        )}
                        {isWeekend(customEndDate) && !getHolidayName(customEndDate) && (
                          <div className="absolute -top-8 left-0 px-2 py-1 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs rounded shadow-md">
                            Weekend
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleApplyCustomRange}
                  disabled={!parseCustomUtcRange(customStartDate, customEndDate)}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Apply Range
                </button>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
});
DateRangeSelector.displayName = 'DateRangeSelector';
